import { countriesLimits } from "configs/limits";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import {
  CountryType,
  getAvailableMirrorDomains,
  getCountriesList,
  handlePaginate,
  handleSelectDomain,
  handleSelectType as onSelectType,
  removeCountryFromDomainList,
} from "store/countries/slice";
import { CountriesHeader } from "./components/CountriesHeader";
import Pagination from "components/Pagination";
import { Table } from "components/table/Table";
import { useTranslation } from "react-i18next";
import style from "./Countries.module.scss";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import { GeoItem } from "components/table/components/Geo/Geo";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

type CountryRow = {
  code: string;
  name: string;
  type: CountryType;
};

const CountriesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [limit, setLimit] = useState<number>(countriesLimits[1]);

  const countriesList = useAppSelector((store) => store.country.list);
  const selectedDomain = useAppSelector(
    (store) => store.country.selectedDomain,
  );
  const availableDomains = useAppSelector(
    (store) => store.country.availableDomains,
  );
  const isDomainsFetching = useAppSelector(
    (store) => store.country.isDomainsFetching,
  );
  const totalItems = useAppSelector((store) => store.country.totalItems);
  const totalPages = useAppSelector((store) => store.country.totalPages);
  const activePage = useAppSelector((store) => store.country.activePage);
  const activeType = useAppSelector((store) => store.country.selectedType);
  const types = useAppSelector((store) => store.country.types);
  const isListFetched = useAppSelector((store) => store.country.isListFetched);
  const processingCountryCode = useAppSelector(
    (store) => store.country.processingItemId,
  );

  const rows: CountryRow[] = countriesList.map((country) => ({
    ...country,
    type: activeType,
  }));

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'country',
    isListFetched
  });

  const permissions = usePermissionHelper();

  useEffect(() => {
    dispatch(getAvailableMirrorDomains());
  }, [dispatch]);

  useEffect(() => {
    if (
      isDomainsFetching ||
      availableDomains.length === 0 ||
      (selectedDomain && availableDomains.includes(selectedDomain))
    ) {
      return;
    }

    dispatch(handleSelectDomain(availableDomains[0]));
  }, [dispatch, availableDomains, isDomainsFetching, selectedDomain]);

  useEffect(() => {
    if (!selectedDomain || isListFetched) return;
    dispatch(
      getCountriesList({
        domain: selectedDomain,
        limit,
        offset: activePage - 1,
        type: activeType,
      }),
    );
  }, [dispatch, selectedDomain, activePage, limit, activeType, isListFetched]);

  const selectType = (type: CountryType) => {
    if (type !== activeType) dispatch(onSelectType(type));
  };

  const deleteFromList = async (countryCode: string) => {
    if (!selectedDomain) return;
    try {
      await dispatch(
        removeCountryFromDomainList({
          domain: selectedDomain,
          type: activeType,
          countryCode,
          totalItems,
        }),
      ).unwrap();
      const pageAfterDelete =
        countriesList.length === 1 && activePage > 1
          ? activePage - 1
          : activePage;
      if (pageAfterDelete !== activePage)
        dispatch(handlePaginate(pageAfterDelete));
      await dispatch(
        getCountriesList({
          domain: selectedDomain,
          limit,
          offset: pageAfterDelete - 1,
          type: activeType,
        }),
      ).unwrap();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const openCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.country}`);
    dispatch(openModal({ activeModal: ModalTypes.country }));
  };

  return (
    <div className="page-list-wrapper page-list--wo-filter">
      <CountriesHeader
        selectedType={activeType}
        onSelectType={selectType}
        types={types}
        createModal={openCreateModal}
        selectedDomain={selectedDomain}
        domains={availableDomains}
        isDomainsFetching={isDomainsFetching}
        onSelectDomain={(domain) => dispatch(handleSelectDomain(domain))}
        isCreateAllowed={permissions?.create}
        refresh={refresh}
        isRefreshRequested={isRefreshRequested}
      />
      <div className="page-list-content">
        {selectedDomain && !isListFetched && (
          <DataTableSkeleton columnsCount={4} rowsCount={8} />
        )}

        {!selectedDomain && (
          <div className="game-table-wrap">
            <EmptyState message={t("selectDomainToViewCountries")} />
          </div>
        )}

        {selectedDomain && isListFetched && countriesList.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noCountriesFound")} />
          </div>
        )}

        {selectedDomain && isListFetched && countriesList.length > 0 && (
          <div className="game-table-wrap">
            <Table
              data={rows}
              idField="code"
              columns={{
                sortingColumns: {
                  code: { value: t("code"), hasSorting: false },
                  name: { value: t("country"), hasSorting: false },
                  type: { value: t("type"), hasSorting: false },
                },
              }}
              hasCheckbox={false}
              hasEdit={false}
              onDelete={deleteFromList}
              customColumns={(row) => ({
                name: <GeoItem code={row.code} label={row.name} />,
                type: (
                  <span
                    className={`badge badge--${row.type === "white" ? "low" : "blocked"}`}
                  >
                    {t(row.type)}
                  </span>
                ),
              })}
              className={style.table}
              processingId={processingCountryCode}
              optionsLabel={t("actions")}
              hasDelete={permissions?.delete}
              hasOptions={permissions?.delete}
            />
          </div>
        )}

        {selectedDomain && isListFetched && countriesList.length > 0 && (
          <Pagination
            handleLimitClick={(value) => {
              setLimit(value);
              dispatch(handlePaginate(1));
            }}
            limitsArray={countriesLimits}
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={() => {
              if (activePage < totalPages)
                dispatch(handlePaginate(activePage + 1));
            }}
            handlePrevClick={() => {
              if (activePage > 1) dispatch(handlePaginate(activePage - 1));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CountriesList;
